import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Check, AlertTriangle, Zap, GitBranch, Code, MessageSquare } from 'lucide-react';

/**
 * Consistent 6-section template for every integration guide.
 *
 * Usage:
 * <IntegrationDocTemplate
 *   title="Amazon Seller Central"
 *   category="marketplaces"
 *   summary="..."
 *   prerequisites={[...]}
 *   connectSteps={[...]}
 *   syncedData={[...]}
 *   agents={[...]}
 *   troubleshooting={[...]}
 * />
 */
export default function IntegrationDocTemplate({
  title,
  category,
  status = 'live',
  logo,
  summary,
  prerequisites = [],
  connectSteps = [],
  syncedData = [],
  agents = [],
  troubleshooting = [],
  supportLink,
  children,
}) {
  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-[#22304A] bg-[#0D1628]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/docs/integrations" className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-4 h-4" /> All integrations
          </Link>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#3EF1C7]" />
            <span className="font-semibold">{title}</span>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-2 text-xs text-[#64748B] uppercase tracking-wider">{category}</div>
        <div className="flex items-baseline gap-3 mb-4">
          <h1 className="text-4xl font-bold">{title}</h1>
          <span className="text-xs px-2 py-1 rounded bg-[#10B981]/20 text-[#10B981]">
            {status.toUpperCase().replace('_', ' ')}
          </span>
        </div>
        <p className="text-lg text-[#94A3B8] mb-12 max-w-3xl">{summary}</p>

        <Section icon={Zap} title="What you'll connect" color="#3EF1C7">
          <p className="text-[#94A3B8]">{summary}</p>
        </Section>

        {prerequisites.length > 0 && (
          <Section icon={AlertTriangle} title="Prerequisites" color="#FFD166">
            <ul className="space-y-2">
              {prerequisites.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#94A3B8]">
                  <Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {connectSteps.length > 0 && (
          <Section icon={GitBranch} title="Connect in 60 seconds" color="#3EF1C7">
            <ol className="space-y-3">
              {connectSteps.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#3EF1C7]/20 text-[#3EF1C7] flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {i + 1}
                  </span>
                  <div className="text-sm text-[#94A3B8]">{s}</div>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {syncedData.length > 0 && (
          <Section icon={Code} title="What syncs after connection" color="#7A5CFF">
            <ul className="grid md:grid-cols-2 gap-2">
              {syncedData.map((d, i) => (
                <li key={i} className="bg-[#0D1628] border border-[#22304A] rounded-lg p-3">
                  <div className="text-sm font-medium text-white">{d.type}</div>
                  <div className="text-xs text-[#94A3B8] mt-1">{d.detail}</div>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {agents.length > 0 && (
          <Section icon={MessageSquare} title="Agents that act on this data" color="#FFD166">
            <div className="grid md:grid-cols-2 gap-3">
              {agents.map((a, i) => (
                <a
                  key={i}
                  href={a.reputationUrl || `https://commerceindex.ai/agents/${a.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#0D1628] border border-[#22304A] rounded-lg p-3 hover:border-[#3EF1C7]/40 transition"
                >
                  <div className="text-sm font-mono text-[#3EF1C7]">{a.name}</div>
                  <div className="text-xs text-[#94A3B8] mt-1">{a.role}</div>
                </a>
              ))}
            </div>
          </Section>
        )}

        {troubleshooting.length > 0 && (
          <Section icon={AlertTriangle} title="Troubleshooting + status" color="#F55454">
            <div className="space-y-2">
              {troubleshooting.map((t, i) => (
                <details key={i} className="bg-[#0D1628] border border-[#22304A] rounded-lg p-3">
                  <summary className="text-sm font-medium text-white cursor-pointer">{t.question}</summary>
                  <p className="text-xs text-[#94A3B8] mt-2">{t.answer}</p>
                </details>
              ))}
            </div>
          </Section>
        )}

        {children && <div className="mt-8">{children}</div>}

        {supportLink && (
          <div className="mt-12 pt-8 border-t border-[#22304A]">
            <p className="text-sm text-[#94A3B8]">
              Need help? <a href={supportLink} className="text-[#3EF1C7] hover:underline">Contact support →</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, color, children }) {
  return (
    <div className="mb-10">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5" style={{ color }} />
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>
      <div className="ml-7">{children}</div>
    </div>
  );
}
